import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, User, Lock, Copy, MessageCircle, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROF_TYPES = [
  { value: 'medico', label: 'Medico' },
  { value: 'veterinario', label: 'Veterinario' },
  { value: 'naturopata', label: 'Naturopata' },
  { value: 'operatore_olistico', label: 'Operatore olistico' },
  { value: 'erborista_parafarmacia', label: 'Erborista / parafarmacia' },
  { value: 'informatore_farmaceutico', label: 'Informatore farmaceutico' },
  { value: 'marketer_influencer', label: 'Marketer / influencer' },
];

export default function ProfilePage() {
  const { user, updateProfile, changePassword } = useAuth();
  const profile = user?.profile;

  const [name, setName] = useState(profile?.full_name || '');
  const [profType, setProfType] = useState(profile?.professional_type || '');
  const [saving, setSaving] = useState(false);

  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  const [wa, setWa] = useState(null);
  const [waTel, setWaTel] = useState('');
  const [waSaving, setWaSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('biounica_token');
    if (token) {
      const h = { Authorization: `Bearer ${token}` };
      axios.get(`${API}/me/whatsapp`, { headers: h })
        .then(r => {
          setWa(r.data);
          setWaTel(r.data?.conversazione?.telefono || r.data?.telefono_suggerito || '');
        })
        .catch(() => {});
    }
  }, []);

  const salvaWhatsapp = async (consenso) => {
    setWaSaving(true);
    try {
      const token = localStorage.getItem('biounica_token');
      const h = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/me/whatsapp/opt-in`, { telefono: waTel, consenso }, { headers: h });
      const r = await axios.get(`${API}/me/whatsapp`, { headers: h });
      setWa(r.data);
      toast.success(consenso ? 'Numero aggiornato: ti scriveremo solo per cose utili.' : 'Non ti scriveremo più su WhatsApp.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Non è stato possibile salvare la scelta');
    } finally {
      setWaSaving(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ full_name: name, professional_type: profType });
      toast.success('Profilo aggiornato con successo');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error('Le password non corrispondono');
      return;
    }
    if (newPw.length < 8) {
      toast.error('La password deve contenere almeno 8 caratteri');
      return;
    }
    setChangingPw(true);
    try {
      await changePassword(newPw);
      toast.success('Password aggiornata con successo');
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setChangingPw(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(profile?.affiliate_code || '');
      toast.success('Codice copiato!');
    } catch {
      toast.error('Impossibile copiare');
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#12100c]">Il mio profilo</h1>
        <p className="text-sm text-[#4a4238] mt-1">Gestisci le tue informazioni personali</p>
      </div>

      {/* Profile Info */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-[#12100c] text-base">
            <User className="w-4 h-4" />
            Informazioni personali
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div>
              <Label className="text-[#12100c] font-medium text-sm">Nome completo</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5 rounded-xl border-gray-300 focus:border-[#c6a15b] focus:ring-[#c6a15b]"
                data-testid="profile-fullname-input"
              />
            </div>

            <div>
              <Label className="text-[#12100c] font-medium text-sm">Email</Label>
              <Input
                value={profile?.email || ''}
                disabled
                className="mt-1.5 rounded-xl border-gray-200 bg-gray-50 text-[#4a4238]"
                data-testid="profile-email-input"
              />
            </div>

            <div>
              <Label className="text-[#12100c] font-medium text-sm">Tipo di professionista</Label>
              <Select value={profType} onValueChange={setProfType}>
                <SelectTrigger
                  className="mt-1.5 rounded-xl border-gray-300 focus:border-[#c6a15b] focus:ring-[#c6a15b]"
                  data-testid="profile-professional-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROF_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#12100c] font-medium text-sm">Codice promotore</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={profile?.affiliate_code || ''}
                  disabled
                  className="rounded-xl border-gray-200 bg-gray-50 text-[#4a4238] font-mono"
                  data-testid="profile-affiliate-code-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={copyCode}
                  className="rounded-xl border-gray-300 shrink-0"
                  data-testid="profile-copy-code-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-[#4a4238] mt-1">Il codice promotore non è modificabile</p>
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full px-6"
              data-testid="profile-save-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Salva modifiche
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Avvisi su WhatsApp */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-[#12100c] text-base">
            <MessageCircle className="w-4 h-4" />
            Avvisi su WhatsApp
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#4a4238] mb-4">
            Pochi messaggi, al massimo uno al mese: quando esce un contenuto nuovo nel portale
            e quando maturi una provvigione. Puoi fermarli quando vuoi, da qui o rispondendo STOP.
          </p>
          {wa?.attivi !== false ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 text-sm text-[#4a5f43] bg-[#5f7d52]/10 rounded-full px-3 py-1.5">
                  <Check className="w-4 h-4" />
                  {waTel ? `Attivi sul numero ${waTel}` : 'Attivi'}
                </span>
                <Button variant="ghost" size="sm" disabled={waSaving}
                        onClick={() => salvaWhatsapp(false)} className="text-[#4a4238]"
                        data-testid="profile-whatsapp-stop-btn">
                  Non voglio riceverli
                </Button>
              </div>
              <div>
                <Label className="text-[#12100c] font-medium text-sm">Numero WhatsApp</Label>
                <div className="flex gap-2 mt-1.5 max-w-md">
                  <Input
                    value={waTel}
                    onChange={(e) => setWaTel(e.target.value)}
                    placeholder="es. 333 1234567"
                    className="rounded-xl border-gray-300 focus:border-[#c6a15b] focus:ring-[#c6a15b]"
                    data-testid="profile-whatsapp-input"
                  />
                  <Button variant="outline" disabled={waSaving || !waTel}
                          onClick={() => salvaWhatsapp(true)} className="rounded-full shrink-0">
                    {waSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aggiorna'}
                  </Button>
                </div>
                <p className="text-xs text-[#5b5346] mt-1.5">
                  È il numero che risulta nei nostri contatti: correggilo se non è più quello.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#4a4238]">
                Al momento non ricevi messaggi su WhatsApp.
              </p>
              <div className="flex gap-2 max-w-md">
                <Input
                  value={waTel}
                  onChange={(e) => setWaTel(e.target.value)}
                  placeholder="es. 333 1234567"
                  className="rounded-xl border-gray-300 focus:border-[#c6a15b] focus:ring-[#c6a15b]"
                  data-testid="profile-whatsapp-input"
                />
                <Button
                  disabled={waSaving || !waTel}
                  onClick={() => salvaWhatsapp(true)}
                  className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full px-6 shrink-0"
                  data-testid="profile-whatsapp-optin-btn"
                >
                  {waSaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Riattiva
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-[#12100c] text-base">
            <Lock className="w-4 h-4" />
            Cambia password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label className="text-[#12100c] font-medium text-sm">Nuova password</Label>
              <Input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="Min. 8 caratteri"
                required
                minLength={8}
                className="mt-1.5 rounded-xl border-gray-300 focus:border-[#c6a15b] focus:ring-[#c6a15b]"
                data-testid="profile-new-password-input"
              />
            </div>
            <div>
              <Label className="text-[#12100c] font-medium text-sm">Conferma nuova password</Label>
              <Input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                placeholder="Ripeti la nuova password"
                required
                minLength={8}
                className="mt-1.5 rounded-xl border-gray-300 focus:border-[#c6a15b] focus:ring-[#c6a15b]"
                data-testid="profile-confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={changingPw}
              variant="outline"
              className="rounded-full px-6 border-[#12100c] text-[#12100c] hover:bg-[#12100c] hover:text-white"
              data-testid="profile-change-password-btn"
            >
              {changingPw && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Aggiorna password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
